import useStyles from "./styles";
import { CustomTypography } from "../../../../components/CustomTypography/CustomTypography";
import { CustomIconButton } from "../../../../components/CustomIconButton/CustomIconButton";
import { useTranslation } from "react-i18next";
import { IconWithBackground } from "../../../../components/IconWithBackground/IconWithBackground";
import { CustomButtonModal } from "../../../../components/CustomButtonModal/CustomButtonModal";
import { useNavigate, useParams } from "react-router";
import { useGetOneVehicle } from "../../../../api/vehicule";
import { getCurrentFleetId } from "../../../../utils/getCurrentFleet";
import { getAssignedCollaborator } from "../../../../utils/assignedCollaborator";
import toast from "react-hot-toast";

type Props = {};

export const Collaborator: React.FC<Props> = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { id = "" } = useParams();
  const vehicleId = parseInt(id);
  const { vehicule } = useGetOneVehicle(getCurrentFleetId(), vehicleId);

  // BUGFIX (collaborator not shown on vehicle detail):
  //   The backend refactor moved `firstname`, `lastname` and `userId` from
  //   the response root into a nested `assignedUser` object, but this
  //   component and the TVehiculeOne type were never updated. As a result
  //   `vehicule.firstname` was always undefined and the UI showed
  //   "No collaborator assigned" even after a successful assignment.
  //
  //   Read the nested shape first (new contract), then fall back to the
  //   flat fields so the UI keeps working across backend versions and even
  //   if some deployments still expose only the legacy shape.
  // La forme de la reponse est resolue dans utils/assignedCollaborator.ts,
  // partage avec le panneau Kilometrage. Ce composant en gardait une copie
  // privee : c'est exactement pour ca que Mileage.tsx est reste sur l'ancien
  // champ `vehicule.userId` et que son bouton de relance etait mort.
  const { userId, hasCollaborator, fullName } =
    getAssignedCollaborator(vehicule);

  const collaboratorName = hasCollaborator
    ? fullName
    : t("panel.collaborator.noCollaborator", "No collaborator assigned");

  /**
   * `brandModel` comes back as a string on some backend versions and as a
   * nested {id,name,brand} object on others (see TVehiculeOne), and a third
   * shape is a JSON string of that object. Normalise to a display name so the
   * label we hand to the wizard never renders as "[object Object]".
   */
  const modelName = (() => {
    const raw: any = vehicule?.brandModel;
    if (!raw) return "";
    if (typeof raw === "object") return raw?.name ?? "";
    try {
      return JSON.parse(raw)?.name ?? raw;
    } catch {
      return raw;
    }
  })();

  /**
   * Carry THIS vehicle into the collaborator wizard.
   *
   * Step 3 of the wizard used to make the user search for a vehicle again,
   * even though they had just clicked "+" from that exact vehicle's sheet -
   * and its search only lists FREE vehicles, so a vehicle in maintenance or
   * already affected could not be found at all from here. Same `presetVehicle`
   * route state the claim form already takes from this page
   * (UpdateVehicule.handleAddClaim), so the two flows stay consistent.
   */
  const onClickAddCollaborator = () => {
    navigate("/collaborator/create", {
      state: {
        presetVehicle: {
          vehicleId,
          brand: vehicule?.brand,
          brandModel: modelName,
          immatriculation: vehicule?.immatriculation,
        },
      },
    });
  };

  return (
    <div className={classes.container}>
      <div className={classes.subContainer}>
        <CustomTypography
          isUppercase
          text={t("panel.collaborator.collaborator", "Collaborator")}
          variant={"h5"}
          styles={{ containerText: classes.contratTitle }}
        />
        <CustomIconButton
          iconName={"plus"}
          widthIcon={"20px"}
          heightIcon={"20px"}
          onClick={onClickAddCollaborator}
        />
      </div>
      <div className={classes.subContainerIcon}>
        <div className={classes.subContainer2}>
          <IconWithBackground iconName={"collaborateur"} />
          <div>
            <CustomTypography
              text={collaboratorName}
              isUppercase
              variant="link"
              fontWeight={"bold"}
            />

            <CustomTypography
              isUppercase
              text={userId ? `ID ${userId}` : "—"}
              variant="link"
              lineHeight={"19px"}
            />
          </div>
        </div>
        <div className={classes.subContainer2}>
          <CustomButtonModal
            variant={"default"}
            textVariant={"link"}
            label={t(
              "panel.collaborator.seeCollaborator",
              "See the collaborator"
            )}
            onClick={() => {
              if (userId) {
                navigate(`/collaborator/update/${userId}`);
              } else {
                toast.error(
                  t(
                    "panel.collaborator.noCollaboratorAssigned",
                    "No collaborator is assigned to this vehicle yet"
                  )
                );
              }
            }}
            width={"fit-content"}
            textDecoration={"none"}
            iconName={"eye"}
            padding={"0px 10px 0px 10px"}
          />
        </div>
      </div>
    </div>
  );
};
